<script setup>
import { computed, ref, watch } from 'vue';
import LiveScoreboard from '@/components/scoring/LiveScoreboard.vue';
import TeamTapButton from '@/components/scoring/TeamTapButton.vue';

// The scorer's input surface. Which pad renders is decided by the SCHEMA (never
// hardcoded per sport): countdown counter -> X01 keypad; accumulator -> break pad with
// snooker-coloured ball buttons; one-side-per-unit leaf -> ends pad; otherwise the
// rally tap zones. Every input group says what tapping it does — no unlabelled buttons.
const props = defineProps({
  scoring: { type: Object, required: true }, // useLiveScoring() (or useScoringPreview() in preview)
  homeName: { type: String, default: 'Home' },
  awayName: { type: String, default: 'Away' },
  // team rosters [{id, name}] — with more than one player per team, rich-data events
  // (180s, goals...) must be attributed to a specific player
  homePlayers: { type: Array, default: () => [] },
  awayPlayers: { type: Array, default: () => [] },
  homeAvatar: { type: String, default: null },
  awayAvatar: { type: String, default: null },
  sportImageUrl: { type: String, default: null }, // scoreboard backdrop
  preview: { type: Boolean, default: false }, // admin preview: no session, no save button
});

const emit = defineEmits(['finalise']);

const schema = computed(() => props.scoring.schema.value);
const state = computed(() => props.scoring.state.value);

const activeLeafUnit = computed(() => {
  const path = state.value?.path ?? [];
  const leafScore = path[path.length - 1];
  return leafScore ? schema.value?.units?.[leafScore.unit] : null;
});

const activeCounter = computed(() => {
  const scoredBy = activeLeafUnit.value?.scoredBy;
  return scoredBy ? { name: scoredBy, ...schema.value.counters[scoredBy] } : null;
});

const padType = computed(() => {
  if (!state.value || state.value.completed) return 'done';
  if (!state.value.setupComplete) return 'setup';
  if (activeCounter.value?.type === 'countdown') return 'countdown';
  if (activeCounter.value?.type === 'accumulator') return 'accumulator';
  if (activeLeafUnit.value?.leafIncrement?.oneSidePerUnit) return 'ends';
  return 'rally';
});

// the full question, phrased per role — a schema roleLabel (e.g. bowls' "Who won the
// toss?") is used verbatim; otherwise wording comes from the role itself
const setupQuestion = computed(() => {
  const turns = schema.value?.turns;
  if (turns?.roleLabel) return turns.roleLabel;
  switch (turns?.role) {
    case 'thrower': return 'Who throws first?';
    case 'server': return 'Who serves first?';
    case 'jack': return 'Who has the jack first?';
    case 'break': return 'Who breaks off?';
    default: return 'Who goes first?';
  }
});

const setupCaption = computed(() => {
  switch (schema.value?.turns?.role) {
    case 'thrower': return 'Throws first';
    case 'server': return 'Serves first';
    case 'jack': return 'Takes the jack';
    case 'break': return 'Breaks off';
    default: return 'Goes first';
  }
});

const turnName = computed(() =>
    state.value?.currentTurnSide === 'away' ? props.awayName : props.homeName);

// what a single scoring tap means, in the sport's own words
const leafLabel = computed(() => activeLeafUnit.value?.label ?? 'Point');

// annotation buttons: schema events that are NOT auto-detected (auto ones need no button)
const manualEvents = computed(() =>
  (schema.value?.events ?? []).filter((e) => !e.autoDetect));

// A scoring event on the ACTIVE leaf (football's Goal, affectsScore) IS the
// scoring tap — rendering a separate +1 zone would double-count. Opponent-
// scored events (own goal) stay in the footer with the other annotations.
const scoringEvents = computed(() => {
  const leafName = state.value?.path?.[state.value.path.length - 1]?.unit;
  return (schema.value?.events ?? []).filter((e) =>
      e.affectsScore && !e.affectsScore.opponent && e.affectsScore.unit === leafName);
});
const footerEvents = computed(() =>
    manualEvents.value.filter((e) => !e.affectsScore));

// leafIncrement.values drives one tap zone per value (basketball's 1/2/3);
// plain rally sports keep the single +1.
const rallyValues = computed(() => {
  const inc = activeLeafUnit.value?.leafIncrement;
  return !inc?.oneSidePerUnit && inc?.values?.length ? inc.values : [1];
});

// A manual-ended leaf needs an explicit end control on this pad too (full
// time); ending level is blocked when the mapping has no draw — keep playing
// (overtime) until someone leads.
const manualLeaf = computed(() => activeLeafUnit.value?.winCondition?.type === 'manual');
const hasDrawOutcome = computed(() =>
    (schema.value?.resultMapping?.map ?? []).some((m) => m.outcome === 'draw'));
const leafTied = computed(() => {
  const leaf = state.value?.path?.[state.value.path.length - 1];
  return leaf != null && leaf.home === leaf.away;
});
const manualEndBlocked = computed(() => manualLeaf.value && leafTied.value && !hasDrawOutcome.value);

// ── goal flow (scoring events) ─────────────────────────────────────────
// Tapping the side that scored opens ONE list: that team's players, Own
// goal, or Scorer not recorded — never the generic side-toggle dialog
// (recording a goal IS picking the scorer; the side was the tap).
const goalDialog = ref(false);
const goalDef = ref(null);
const goalSide = ref('home');
const goalSidePlayers = computed(() =>
    goalSide.value === 'home' ? props.homePlayers : props.awayPlayers);
const goalSideName = computed(() =>
    goalSide.value === 'home' ? props.homeName : props.awayName);
// the opponent-flip counterpart on the same unit (football's ownGoal): the
// event side carries the CONCEDING team, the score lands with the scorer's
const ownGoalDef = computed(() =>
    (schema.value?.events ?? []).find((e) =>
        e.affectsScore?.opponent && e.affectsScore.unit === goalDef.value?.affectsScore?.unit) ?? null);

// Own goals are put in by a player on the OTHER team (the side that concedes the
// credit) — so the own-goal scorer is picked from the opposite roster.
const goalOwnGoalStep = ref(false);
const ownGoalConcedingSide = computed(() => (goalSide.value === 'home' ? 'away' : 'home'));
const ownGoalSidePlayers = computed(() =>
    ownGoalConcedingSide.value === 'home' ? props.homePlayers : props.awayPlayers);
const ownGoalSideName = computed(() =>
    ownGoalConcedingSide.value === 'home' ? props.homeName : props.awayName);

const openScoringEvent = (def, side) => {
  goalDef.value = def;
  goalSide.value = side;
  goalOwnGoalStep.value = false;
  goalDialog.value = true;
};
const recordGoalScorer = (playerId) => {
  props.scoring.annotate(goalDef.value.key, { side: goalSide.value, ...(playerId ? { playerId } : {}) });
  goalDialog.value = false;
};
const recordOwnGoal = (playerId) => {
  props.scoring.annotate(ownGoalDef.value.key, {
    side: ownGoalConcedingSide.value,
    ...(playerId ? { playerId } : {}),
  });
  goalDialog.value = false;
};

// --- countdown (X01) keypad ---
const visitInput = ref('');
const remaining = computed(() => {
  const counter = state.value?.counters?.[activeLeafUnit.value?.scoredBy];
  if (!counter) return null;
  return state.value.currentTurnSide === 'away' ? counter.awayRemaining : counter.homeRemaining;
});
const visitValue = computed(() => (visitInput.value === '' ? null : Number(visitInput.value)));
const visitTooHigh = computed(() => visitValue.value != null && visitValue.value > 180);
const wouldBust = computed(() => {
  if (visitValue.value == null || remaining.value == null) return false;
  const next = remaining.value - visitValue.value;
  return next < 0 || next === 1;
});
const pressDigit = (digit) => {
  if (visitInput.value.length >= 3) return;
  visitInput.value = `${visitInput.value}${digit}`.replace(/^0+(?=\d)/, '');
};
const backspace = () => {
  visitInput.value = visitInput.value.slice(0, -1);
};
const submitVisit = (total) => {
  const value = Number(total);
  if (!Number.isFinite(value) || value < 0 || value > 180) return;
  props.scoring.visit(state.value.currentTurnSide ?? 'home', value);
  visitInput.value = '';
};
// common three-dart totals, labelled — plus explicit no-score
const quickTotals = [26, 41, 45, 60, 85, 100, 140, 180];

// --- accumulator (break) pad ---
// snooker ball colours: the values ARE the balls, so show them as balls
const ballStyles = {
  1: { bg: '#C8102E', fg: '#fff' }, // red
  2: { bg: '#F3C300', fg: '#3A2E00' }, // yellow
  3: { bg: '#00843D', fg: '#fff' }, // green
  4: { bg: '#7B4B28', fg: '#fff' }, // brown
  5: { bg: '#0057B7', fg: '#fff' }, // blue
  6: { bg: '#E86FA4', fg: '#fff' }, // pink
  7: { bg: '#1F1F1F', fg: '#fff' }, // black
};
const ballValues = computed(() => activeCounter.value?.values ?? [1, 2, 3, 4, 5, 6, 7]);
const foulValues = computed(() => activeCounter.value?.foul?.awardOpponent ?? []);
const breakSide = ref('home');
const breakSideName = computed(() => (breakSide.value === 'home' ? props.homeName : props.awayName));

// the engine owns whose turn it is — a foul or a miss hands the table to the opponent,
// so the pad follows currentTurnSide; tapping a scoreboard side corrects it
watch(() => state.value?.currentTurnSide, (side) => {
  if (side === 'home' || side === 'away') breakSide.value = side;
}, { immediate: true });

// scoreboard tap: hand the serve/table/jack to the other side (correction event —
// rotation re-derives from this point)
const switchTurn = (side) => props.scoring.record('correction', { side });

// snooker rules: the engine says which balls are legal right now; the rest disable
const accumulatorState = computed(() =>
    state.value?.counters?.[activeLeafUnit.value?.scoredBy] ?? null);
const allowedBalls = computed(() => accumulatorState.value?.allowedValues ?? null);
const ballAllowed = (value) => allowedBalls.value == null || allowedBalls.value.includes(value);
const redsRemaining = computed(() => accumulatorState.value?.redsRemaining ?? null);

// --- ends pad ---
const endShotValues = computed(() => activeLeafUnit.value?.leafIncrement?.values ?? [1, 2, 3, 4]);
const endSide = ref('home');

// --- event annotation dialog ---
const eventDialog = ref(false);
const eventDef = ref(null);
const eventSide = ref('home');
const eventValue = ref('');
const eventPlayerId = ref(null);

const sidePlayers = computed(() =>
    eventSide.value === 'home' ? props.homePlayers : props.awayPlayers);

// singles attribute automatically; doubles/teams need an explicit pick
const needsPlayerPick = computed(() =>
    (eventDef.value?.playerRequired ?? false) && sidePlayers.value.length > 1);

const playerMissing = computed(() => needsPlayerPick.value && !eventPlayerId.value);

const openEvent = (def) => {
  eventDef.value = def;
  eventSide.value = state.value?.currentTurnSide ?? 'home';
  eventValue.value = '';
  eventPlayerId.value = null;
  eventDialog.value = true;
};

// side change invalidates a picked player from the other team
watch(eventSide, () => {
  eventPlayerId.value = null;
});

const recordEvent = () => {
  if (playerMissing.value) return;
  const payload = { side: eventSide.value };
  if (eventDef.value.valueField && eventValue.value !== '') payload.value = Number(eventValue.value);
  const playerId = eventPlayerId.value
      ?? (sidePlayers.value.length === 1 ? sidePlayers.value[0].id : null);
  if (playerId) payload.playerId = playerId;
  props.scoring.annotate(eventDef.value.key, payload);
  eventDialog.value = false;
};

const syncLabel = computed(() => {
  switch (props.scoring.syncState.value) {
    case 'syncing': return 'Syncing…';
    case 'offline': return `Offline (${props.scoring.pendingCount.value} unsent)`;
    case 'error': return 'Sync problem';
    default: return 'Synced';
  }
});
const syncColor = computed(() => {
  switch (props.scoring.syncState.value) {
    case 'offline': return 'warning';
    case 'error': return 'error';
    default: return 'success';
  }
});
</script>

<template>
  <div class="live-counter">
    <div class="d-flex align-center justify-space-between mb-2">
      <v-chip v-if="!preview" size="small" :color="syncColor" variant="tonal">{{ syncLabel }}</v-chip>
      <v-chip v-else size="small" variant="tonal">Preview</v-chip>
      <v-btn size="small" variant="text" prepend-icon="mdi-undo" :disabled="padType === 'setup'" @click="scoring.undo()">
        Undo
      </v-btn>
    </div>

    <LiveScoreboard :state="state" :schema="schema" :home-name="homeName" :away-name="awayName"
                    :background-url="sportImageUrl" :interactive="padType !== 'setup'"
                    @switch-side="switchTurn" />

    <!-- setup: initial role selection -->
    <div v-if="padType === 'setup'" class="text-center my-4">
      <div class="text-subtitle-1 font-weight-bold mb-1">{{ setupQuestion }}</div>
      <div class="text-caption text-medium-emphasis mb-4">
        Turn order is tracked automatically from here.
      </div>
      <div class="d-flex ga-2">
        <TeamTapButton :name="homeName" :avatar-url="homeAvatar" :caption="setupCaption"
                       @click="scoring.setup('home')" />
        <TeamTapButton :name="awayName" :avatar-url="awayAvatar" :caption="setupCaption"
                       @click="scoring.setup('away')" />
      </div>
    </div>

    <template v-else-if="padType !== 'done'">
      <!-- same 24px gap as between sections: scoreboard -> input zone -->
      <div class="mt-6" />

      <!-- rally / point tap zones -->
      <div v-if="padType === 'rally'">
        <!-- schema scoring events (Goal) are THE scoring tap; the dialog takes
             the scorer + assist and the engine moves the score itself -->
        <div v-for="def in scoringEvents" :key="def.key" class="lc-section">
          <div class="lc-eyebrow">{{ def.label }}: tap the team that scored</div>
          <div class="d-flex ga-2">
            <TeamTapButton :name="homeName" :avatar-url="homeAvatar" :caption="def.label"
                           @click="openScoringEvent(def, 'home')" />
            <TeamTapButton :name="awayName" :avatar-url="awayAvatar" :caption="def.label"
                           @click="openScoringEvent(def, 'away')" />
          </div>
        </div>
        <div v-if="!scoringEvents.length" class="lc-section">
          <div class="lc-eyebrow">
            {{ rallyValues.length > 1
                ? 'Points scored: tap the team'
                : `Who won the ${leafLabel.toLowerCase() === 'game' ? 'point' : leafLabel.toLowerCase()}?` }}
          </div>
          <div v-for="(value, i) in rallyValues" :key="'v' + value" class="d-flex ga-2" :class="{'mt-2': i > 0}">
            <TeamTapButton :name="homeName" :avatar-url="homeAvatar" :caption="`+${value}`"
                           @click="value === 1 ? scoring.rally('home') : scoring.point('home', value)" />
            <TeamTapButton :name="awayName" :avatar-url="awayAvatar" :caption="`+${value}`"
                           @click="value === 1 ? scoring.rally('away') : scoring.point('away', value)" />
          </div>
        </div>
        <!-- full time: manual-ended units need an explicit end here too (the
             accumulator pad has its own) -->
        <div v-if="manualLeaf" class="lc-section">
          <v-btn block variant="tonal" prepend-icon="mdi-flag-checkered"
                 :disabled="manualEndBlocked" @click="scoring.unitEnd()">
            End {{ leafLabel.toLowerCase() }}
          </v-btn>
          <div v-if="manualEndBlocked" class="text-caption text-medium-emphasis mt-1 text-center">
            Scores level. Keep playing (overtime) until someone leads.
          </div>
        </div>
      </div>

      <!-- X01 visit keypad -->
      <div v-else-if="padType === 'countdown'">
        <div class="lc-section">
          <div class="lc-eyebrow">{{ turnName }}'s visit: total scored with 3 darts</div>

          <div class="lc-visit-display mb-2" :class="{'lc-visit-display--error': visitTooHigh}">
            <span class="lc-visit-value">{{ visitInput === '' ? '0' : visitInput }}</span>
            <span v-if="visitTooHigh" class="lc-visit-hint text-error">Max 180</span>
            <span v-else-if="wouldBust && visitValue" class="lc-visit-hint text-warning">Bust (reverts to {{ remaining }})</span>
            <span v-else-if="visitValue != null && remaining != null" class="lc-visit-hint">leaves {{ remaining - visitValue }}</span>
          </div>

          <div class="lc-keypad">
            <button v-for="digit in [1,2,3,4,5,6,7,8,9]" :key="digit" class="lc-key" @click="pressDigit(digit)">
              {{ digit }}
            </button>
            <button class="lc-key lc-key--muted" @click="backspace">
              <v-icon icon="mdi-backspace-outline" size="20" />
            </button>
            <button class="lc-key" @click="pressDigit(0)">0</button>
            <button class="lc-key lc-key--enter" :disabled="visitValue == null || visitTooHigh" @click="submitVisit(visitValue)">
              <v-icon icon="mdi-check-bold" size="20" />
            </button>
          </div>
        </div>

        <div class="lc-section">
          <div class="lc-eyebrow">Common totals: one tap scores the visit</div>
          <div class="d-flex flex-wrap ga-2">
            <v-btn v-for="total in quickTotals" :key="total" size="small" variant="outlined" @click="submitVisit(total)">
              {{ total }}
            </v-btn>
            <v-btn size="small" variant="outlined" @click="submitVisit(0)">No score</v-btn>
          </div>
        </div>
      </div>

      <!-- break / accumulator pad: who's at the table follows the engine — tap a team on
           the scoreboard above to correct it -->
      <div v-else-if="padType === 'accumulator'">
        <div class="lc-section">
          <div class="lc-eyebrow">
            Ball potted by {{ breakSideName }}
            <span v-if="redsRemaining != null" class="lc-eyebrow-note">· {{ redsRemaining }} reds left</span>
          </div>
          <!-- the colours read as one set laid out on the rail; Miss is an action
               (break over), not a ball, so it gets its own full-width button -->
          <div class="lc-ball-row">
            <button
                v-for="value in ballValues"
                :key="'pot' + value"
                class="lc-ball"
                :class="{'lc-ball--off': !ballAllowed(value)}"
                :disabled="!ballAllowed(value)"
                :style="{ background: ballStyles[value]?.bg ?? 'rgb(var(--v-theme-primary))', color: ballStyles[value]?.fg ?? '#fff' }"
                @click="scoring.pot(breakSide, value)"
            >{{ value }}</button>
          </div>
          <button class="lc-miss" @click="scoring.miss(breakSide)">Miss (break over)</button>
        </div>

        <div v-if="foulValues.length" class="lc-section">
          <div class="lc-eyebrow">Foul by {{ breakSideName }}: points go to the opponent</div>
          <div class="d-flex flex-wrap ga-2">
            <v-btn v-for="value in foulValues" :key="'foul' + value" size="small" color="error" variant="outlined"
                   @click="scoring.foul(breakSide, value)">
              Foul {{ value }}
            </v-btn>
          </div>
        </div>

        <div class="lc-section">
          <v-btn block variant="tonal" prepend-icon="mdi-flag-checkered" @click="scoring.unitEnd()">
            End {{ activeLeafUnit?.label?.toLowerCase() ?? 'frame' }} (leader wins it)
          </v-btn>
        </div>
      </div>

      <!-- ends pad (bowls) -->
      <div v-else-if="padType === 'ends'">
        <div class="lc-section">
          <div class="lc-eyebrow">{{ activeLeafUnit?.label ?? 'End' }} won by</div>
          <v-btn-toggle v-model="endSide" mandatory density="compact" color="primary">
            <v-btn value="home" size="small">{{ homeName }}</v-btn>
            <v-btn value="away" size="small">{{ awayName }}</v-btn>
          </v-btn-toggle>
        </div>

        <div class="lc-section">
          <div class="lc-eyebrow">Shots scored: tap to finish the {{ activeLeafUnit?.label?.toLowerCase() ?? 'end' }}</div>
          <div class="d-flex flex-wrap ga-2">
            <v-btn v-for="value in endShotValues" :key="value" size="small" color="primary" variant="tonal"
                   @click="scoring.endScore(endSide, value)">
              {{ value }}
            </v-btn>
            <v-btn size="small" variant="outlined" @click="scoring.endScore(null, 0)">Dead end (no score)</v-btn>
          </div>
        </div>
      </div>

      <!-- manual event annotations (aces, goals, cards...) -->
      <div v-if="footerEvents.length" class="lc-section lc-section--footer">
        <div class="lc-eyebrow">Record a moment</div>
        <div class="d-flex flex-wrap ga-2">
          <v-btn v-for="def in footerEvents" :key="def.key" size="small" variant="outlined"
                 prepend-icon="mdi-star-outline" @click="openEvent(def)">
            {{ def.label }}
          </v-btn>
        </div>
      </div>
    </template>

    <!-- finished -->
    <div v-else class="my-3">
      <v-btn v-if="!preview" block color="primary" size="large" @click="emit('finalise')">Save result</v-btn>
    </div>

    <tl-dialog v-model="eventDialog" :title="eventDef?.label" :max-width="360">
      <template v-if="eventDef">
        <div class="lc-eyebrow mb-1">Which side?</div>
        <v-btn-toggle v-model="eventSide" mandatory density="compact" color="primary" class="mb-3">
          <v-btn value="home" size="small">{{ homeName }}</v-btn>
          <v-btn value="away" size="small">{{ awayName }}</v-btn>
        </v-btn-toggle>
        <v-select
          v-if="needsPlayerPick"
          v-model="eventPlayerId"
          :items="sidePlayers"
          item-value="id"
          item-title="name"
          density="compact"
          label="Which player?"
          :error="playerMissing"
          :error-messages="playerMissing ? ['Pick the player this belongs to'] : []"
          class="mb-2"
        />
        <v-text-field
          v-if="eventDef.valueField"
          v-model="eventValue"
          type="number"
          density="compact"
          :label="eventDef.label"
          :min="eventDef.valueField.min ?? undefined"
          :max="eventDef.valueField.max ?? undefined"
        />
      </template>
      <template #actions>
        <v-btn variant="text" @click="eventDialog = false">Cancel</v-btn>
        <v-btn color="primary" variant="flat" :disabled="playerMissing" @click="recordEvent">Record</v-btn>
      </template>
    </tl-dialog>

    <tl-dialog v-model="goalDialog"
               :title="goalOwnGoalStep
                 ? `Own goal · ${ownGoalSideName}`
                 : `${goalDef?.label ?? 'Goal'} · ${goalSideName}`"
               :max-width="360">
      <!-- Step 1: who scored for the crediting side (or choose own goal) -->
      <template v-if="goalDef && !goalOwnGoalStep">
        <div class="lc-eyebrow mb-1">Who scored?</div>
        <v-list density="compact" class="pa-0">
          <v-list-item title="Scorer not recorded" prepend-icon="mdi-help-circle-outline"
                       @click="recordGoalScorer(null)" />
          <v-divider class="my-1" />
          <v-list-item v-for="player in goalSidePlayers" :key="player.id"
                       :title="player.name" prepend-icon="mdi-account-outline"
                       @click="recordGoalScorer(player.id)" />
          <v-divider v-if="ownGoalDef" class="my-1" />
          <v-list-item v-if="ownGoalDef" :title="ownGoalDef.label" prepend-icon="mdi-arrow-u-left-bottom"
                       subtitle="Put in by the other team"
                       @click="ownGoalSidePlayers.length ? (goalOwnGoalStep = true) : recordOwnGoal(null)" />
        </v-list>
      </template>

      <!-- Step 2: which player on the conceding side put it in their own net -->
      <template v-else-if="goalDef && goalOwnGoalStep">
        <div class="lc-eyebrow mb-1">Who put it in their own net?</div>
        <v-list density="compact" class="pa-0">
          <v-list-item title="Scorer not recorded" prepend-icon="mdi-help-circle-outline"
                       @click="recordOwnGoal(null)" />
          <v-divider class="my-1" />
          <v-list-item v-for="player in ownGoalSidePlayers" :key="player.id"
                       :title="player.name" prepend-icon="mdi-account-outline"
                       @click="recordOwnGoal(player.id)" />
        </v-list>
      </template>

      <template #actions>
        <v-btn v-if="goalOwnGoalStep" variant="text" @click="goalOwnGoalStep = false">Back</v-btn>
        <v-btn variant="text" @click="goalDialog = false">Cancel</v-btn>
      </template>
    </tl-dialog>
  </div>
</template>

<style scoped>
/* one vertical rhythm for the whole pad: 24px between sections, 10px label-to-control */
.lc-section {
  margin-top: 24px;
}
.lc-section:first-child {
  margin-top: 0;
}
.lc-section--footer {
  padding-top: 16px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

/* section labels: every input group says what tapping it does */
.lc-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.55);
  margin-bottom: 10px;
}
.lc-eyebrow-note {
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
}

/* X01 visit display */
.lc-visit-display {
  display: flex;
  align-items: baseline;
  gap: 10px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  padding: 6px 14px;
}
.lc-visit-display--error {
  border-color: rgb(var(--v-theme-error));
}
.lc-visit-value {
  font-size: 34px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  min-width: 58px;
}
.lc-visit-hint {
  font-size: 12.5px;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

/* X01 keypad — thumb-sized keys, calculator layout */
.lc-keypad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.lc-key {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  padding: 15px 0;
  cursor: pointer;
  transition: background 0.1s;
}
.lc-key:active {
  background: rgba(var(--v-theme-primary), 0.12);
}
.lc-key--muted {
  color: rgba(var(--v-theme-on-surface), 0.55);
}
.lc-key--enter {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  border-color: transparent;
}
.lc-key--enter:disabled {
  opacity: 0.35;
  cursor: default;
}

/* snooker ball buttons — the values are the balls, one row sized to the panel */
.lc-ball-row {
  display: flex;
  gap: 8px;
  justify-content: space-between;
}
.lc-miss {
  display: block;
  width: 100%;
  margin-top: 10px;
  height: 44px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  font-size: 13.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
  cursor: pointer;
}
.lc-miss:hover {
  background: rgb(var(--v-theme-light));
}
.lc-ball {
  width: clamp(34px, calc((100% - 48px) / 7), 48px);
  aspect-ratio: 1;
  height: auto;
  flex: 0 1 48px;
  border-radius: 50%;
  border: none;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: inset 0 -3px 6px rgba(0, 0, 0, 0.25), inset 0 2px 4px rgba(255, 255, 255, 0.28);
  transition: opacity 0.12s, transform 0.08s;
}
.lc-ball:active {
  transform: scale(0.94);
}
/* not legal to pot right now (engine's allowedValues) */
.lc-ball--off {
  opacity: 0.22;
  cursor: default;
  filter: grayscale(0.4);
}

</style>
